import { useEffect, useMemo, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Icon } from './icons';
import { LoginPage } from './LoginPage';
import {
  CrmPrincipal,
  getStoredUser,
  setStoredUser,
  setToken
} from '../lib/api';
import { ROLE_LABELS, type Permission } from '../lib/roles';
import { connectCrmSocket, disconnectCrmSocket } from '../lib/socket';

const NAV: Array<{
  to: string;
  label: string;
  icon: React.ComponentType<{ size?: number }>;
  perm?: Permission;
  end?: boolean;
}> = [
  { to: '/', label: 'Дашборд', icon: Icon.Dashboard, end: true },
  { to: '/users', label: 'Пользователи', icon: Icon.Users, perm: 'users:view' },
  { to: '/rooms', label: 'Комнаты', icon: Icon.Rooms, perm: 'rooms:view' },
  { to: '/earnings', label: 'Заработок', icon: Icon.Cash, perm: 'rake:view' },
  { to: '/peek', label: 'Пик', icon: Icon.Eye, perm: 'rooms:peek' },
  { to: '/audit', label: 'Аудит', icon: Icon.Shield, perm: 'audit:view' },
  { to: '/notifications', label: 'Уведомления', icon: Icon.Bell, perm: 'audit:view' },
  { to: '/team', label: 'Команда CRM', icon: Icon.Crown, perm: 'settings:edit' },
  { to: '/affiliate', label: 'Аффилиат', icon: Icon.Cash, perm: 'affiliate:view' },
  { to: '/affiliates-admin', label: 'Аффилиаты · Админ', icon: Icon.Shield, perm: 'affiliate:manage' }
];

const COLLAPSE_KEY = 'bura_crm_sidebar_collapsed';

export function AppShell() {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState<CrmPrincipal | null>(() => getStoredUser());
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem(COLLAPSE_KEY) === '1';
  });

  useEffect(() => {
    if (user) connectCrmSocket();
    return () => disconnectCrmSocket();
  }, [user]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem(COLLAPSE_KEY, collapsed ? '1' : '0');
    }
  }, [collapsed]);

  if (!user) {
    return <AuthGate onAuthed={setUser} />;
  }

  // Аффилиат не должен видеть чужую админку — редиректим его на свой раздел.
  useEffect(() => {
    if (user.role === 'affiliate' && (location.pathname === '/' || location.pathname === '/login')) {
      navigate('/affiliate', { replace: true });
    }
  }, [user, location.pathname, navigate]);

  const items = NAV.filter((n) => !n.perm || user.permissions.includes(n.perm));

  const logout = () => {
    setToken(null);
    setStoredUser(null);
    setUser(null);
    disconnectCrmSocket();
    navigate('/login', { replace: true });
  };

  return (
    <div className="min-h-screen flex bg-stone-950 text-stone-100">
      <Sidebar items={items} collapsed={collapsed} onToggle={() => setCollapsed((v) => !v)} onLogout={logout} user={user} />
      <main className="flex-1 min-w-0 flex flex-col">
        <Topbar title={items.find((i) => location.pathname === i.to)?.label ?? 'BuraClub CRM'} user={user} />
        <div className="flex-1 p-4 md:p-6 lg:p-8 anim-fade overflow-x-hidden">
          <Outlet />
        </div>
      </main>
    </div>
  );
}

function Sidebar({
  items,
  collapsed,
  onToggle,
  onLogout,
  user
}: {
  items: typeof NAV;
  collapsed: boolean;
  onToggle: () => void;
  onLogout: () => void;
  user: CrmPrincipal;
}) {
  return (
    <aside
      className={`${collapsed ? 'w-[68px]' : 'w-[240px]'} shrink-0 sticky top-0 h-screen glass-strong border-r border-white/5 flex flex-col transition-[width] duration-200 z-20`}
    >
      <div className="flex items-center gap-2 px-4 h-16 border-b border-white/5">
        <Logo collapsed={collapsed} />
        {!collapsed && (
          <div className="leading-tight">
            <div className="text-[15px] font-bold tracking-tight text-gradient-gold">BuraClub</div>
            <div className="text-[10px] uppercase tracking-widest text-stone-500">CRM</div>
          </div>
        )}
        <button
          onClick={onToggle}
          aria-label="Свернуть меню"
          className="ml-auto text-stone-400 hover:text-white p-1 rounded-md hover:bg-white/5"
        >
          {collapsed ? <Icon.ChevronsRight size={16} /> : <Icon.ChevronsLeft size={16} />}
        </button>
      </div>

      <nav className="flex-1 overflow-y-auto py-3">
        {items.map((it) => (
          <NavLink
            key={it.to}
            to={it.to}
            end={it.end}
            className={({ isActive }) =>
              `group flex items-center gap-3 mx-2 my-0.5 px-3 py-2.5 rounded-xl text-sm transition-colors ${
                isActive
                  ? 'bg-amber-400/10 text-amber-300 ring-1 ring-amber-400/20'
                  : 'text-stone-300 hover:text-white hover:bg-white/5'
              }`
            }
          >
            <it.icon size={18} />
            {!collapsed && <span className="truncate">{it.label}</span>}
          </NavLink>
        ))}
      </nav>

      <div className="border-t border-white/5 p-3">
        <div className={`flex items-center gap-2 ${collapsed ? 'justify-center' : ''}`}>
          <div className="w-8 h-8 rounded-full bg-amber-400/15 text-amber-300 flex items-center justify-center text-sm font-semibold ring-1 ring-amber-400/20">
            {user.displayName.charAt(0).toUpperCase()}
          </div>
          {!collapsed && (
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium truncate">{user.displayName}</div>
              <div className="text-[11px] text-stone-500 truncate">{ROLE_LABELS[user.role]}</div>
            </div>
          )}
          {!collapsed && (
            <button
              onClick={onLogout}
              className="p-1.5 rounded-md text-stone-400 hover:text-white hover:bg-white/5"
              title="Выйти"
              aria-label="Выйти"
            >
              <Icon.Logout size={16} />
            </button>
          )}
        </div>
        {collapsed && (
          <button
            onClick={onLogout}
            className="mt-2 w-full p-2 rounded-md text-stone-400 hover:text-white hover:bg-white/5 flex items-center justify-center"
            title="Выйти"
            aria-label="Выйти"
          >
            <Icon.Logout size={16} />
          </button>
        )}
      </div>
    </aside>
  );
}

function Logo({ collapsed }: { collapsed: boolean }) {
  return (
    <div className="relative w-9 h-9 shrink-0">
      <div className="absolute inset-0 rounded-lg bg-gradient-to-br from-amber-300 to-amber-700 shadow-[0_0_18px_-2px_rgba(251,191,36,0.5)]" />
      <div className="absolute inset-[2px] rounded-md bg-stone-950 flex items-center justify-center text-amber-300 font-display font-bold text-[15px]">
        БК
      </div>
    </div>
  );
}

function Topbar({ title, user }: { title: string; user: CrmPrincipal }) {
  return (
    <div className="h-16 sticky top-0 z-10 glass-strong border-b border-white/5 flex items-center px-4 md:px-6 gap-3">
      <h1 className="text-lg md:text-xl font-semibold tracking-tight truncate">{title}</h1>
      <div className="ml-auto flex items-center gap-2">
        <RolePill role={user.role} />
        <Clock />
      </div>
    </div>
  );
}

function RolePill({ role }: { role: CrmPrincipal['role'] }) {
  const cls = useMemo(() => {
    switch (role) {
      case 'super_admin': return 'pill pill-amber';
      case 'admin': return 'pill pill-sky';
      case 'dealer_manager': return 'pill pill-green';
      default: return 'pill pill-stone';
    }
  }, [role]);
  return <span className={cls}><Icon.Crown size={11} />{ROLE_LABELS[role]}</span>;
}

function Clock() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 30_000);
    return () => clearInterval(t);
  }, []);
  return (
    <span className="hidden sm:inline-flex items-center gap-2 text-xs text-stone-400 font-mono">
      {now.toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
    </span>
  );
}

// === Auth gate ===

function AuthGate({ onAuthed }: { onAuthed: (u: CrmPrincipal) => void }) {
  return <OutletContext onAuthed={onAuthed} />;
}

function OutletContext({ onAuthed }: { onAuthed: (u: CrmPrincipal) => void }) {
  const navigate = useNavigate();
  const location = useLocation();
  return (
    <LoginPage
      onAuthed={(u) => {
        onAuthed(u);
        const target = (location.state as any)?.from?.pathname ?? '/';
        navigate(target, { replace: true });
      }}
    />
  );
}
